const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const formatGenderLabel = (gender) => {
  const g = String(gender || '').trim().toLowerCase();
  if (g === 'male') return 'männlich';
  if (g === 'female') return 'weiblich';
  return 'neutral';
};

export const parseContactGender = (raw) => {
  const g = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (['male', 'm', 'männlich', 'maennlich', 'herr', 'man', 'mann'].includes(g)) return 'male';
  if (['female', 'f', 'weiblich', 'frau', 'woman', 'w'].includes(g)) return 'female';
  return 'unknown';
};

const formatCategories = (contact) => {
  const list = Array.isArray(contact?.categories)
    ? contact.categories
    : Array.isArray(contact?.categoryNames)
      ? contact.categoryNames.map((name) => ({ name }))
      : [];
  return list
    .map((cat) => String(cat?.name || cat || '').trim())
    .filter(Boolean)
    .join('; ');
};

export const parseCategoryNames = (raw) =>
  String(raw || '')
    .split(/[;|]/)
    .map((part) => part.trim())
    .filter(Boolean);

/** Export-Header in derselben Reihenfolge wie die Kontaktdetails */
export const CONTACT_EXPORT_HEADERS = [
  'Ansprechpartner',
  'Geschlecht',
  '2. Ansprechpartner',
  'Geschlecht 2',
  'E-Mail',
  'Unternehmen',
  'Telefon',
  'Website',
  'Notizen',
  'Kategorien',
  'Abgemeldet',
  'Erstellt am',
];

export const exportContactsToCSV = (contacts) => {
  const rows = (contacts || []).map((c) => [
    csvEscape(c.name || ''),
    csvEscape(formatGenderLabel(c.gender)),
    csvEscape(c.name2 || ''),
    csvEscape(c.name2 ? formatGenderLabel(c.gender2) : ''),
    csvEscape(c.email || ''),
    csvEscape(c.company || ''),
    csvEscape(c.phone || ''),
    csvEscape(c.website || ''),
    csvEscape(c.notes || ''),
    csvEscape(formatCategories(c)),
    csvEscape(c.unsubscribed ? 'Ja' : 'Nein'),
    csvEscape(c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE') : ''),
  ]);
  const csv = [CONTACT_EXPORT_HEADERS.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `kontakte_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const parseCsvLine = (line, delimiter = ',') => {
  const sep = delimiter === ';' ? ';' : delimiter === '\t' ? '\t' : ',';
  const values = [];
  let current = '';
  let inQuotes = false;
  for (let j = 0; j < line.length; j++) {
    const ch = line[j];
    if (ch === '"') {
      if (inQuotes && line[j + 1] === '"') {
        current += '"';
        j++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === sep && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current.trim());
  return values;
};

/** Excel DE nutzt oft `;`, US/Standard `,` — anhand der Kopfzeile erkennen */
const detectCsvDelimiter = (text) => {
  const firstLine =
    String(text || '')
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .find((l) => l.trim()) || '';
  let commas = 0;
  let semis = 0;
  let tabs = 0;
  let inQuotes = false;
  for (let i = 0; i < firstLine.length; i++) {
    const ch = firstLine[i];
    if (ch === '"') {
      if (inQuotes && firstLine[i + 1] === '"') {
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === ',') commas += 1;
    else if (ch === ';') semis += 1;
    else if (ch === '\t') tabs += 1;
  }
  if (tabs > commas && tabs > semis) return '\t';
  if (semis > commas) return ';';
  if (commas > 0) return ',';
  if (semis > 0) return ';';
  return ',';
};

const looksLikeEmail = (value) => /.+@.+\..+/.test(String(value || '').trim());

const headerIndex = (headers, aliases) => {
  const normalized = headers.map((h) =>
    String(h || '')
      .toLowerCase()
      .replace(/^\uFEFF/, '')
      .trim()
  );
  for (const alias of aliases) {
    const i = normalized.findIndex(
      (h) => h === alias || h.replace(/\s+/g, '') === alias.replace(/\s+/g, '')
    );
    if (i >= 0) return i;
  }
  return -1;
};

const cellToString = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString('de-DE');
  }
  return String(value).trim();
};

/** Gemeinsames Mapping aus Header + Datenzeilen (CSV/XLSX) */
export const mapContactRowsFromTable = (headers, dataRows) => {
  const headerList = (headers || []).map((h) => cellToString(h));
  const cols = {
    name: headerIndex(headerList, [
      'ansprechpartner',
      'name',
      'kontakt',
      'vollständiger name',
      'voller name',
    ]),
    gender: headerIndex(headerList, ['geschlecht', 'gender', 'anrede']),
    name2: headerIndex(headerList, [
      '2. ansprechpartner',
      'zweiter ansprechpartner',
      '2 ansprechpartner',
      'name2',
    ]),
    gender2: headerIndex(headerList, [
      'geschlecht 2',
      'geschlecht2',
      'gender2',
      '2. geschlecht',
      'anrede 2',
    ]),
    email: headerIndex(headerList, ['e-mail', 'email', 'e mail', 'mail']),
    company: headerIndex(headerList, ['unternehmen', 'company', 'firma', 'firm']),
    phone: headerIndex(headerList, ['telefon', 'phone', 'tel', 'handy', 'mobil']),
    website: headerIndex(headerList, ['website', 'webseite', 'url', 'homepage']),
    notes: headerIndex(headerList, ['notizen', 'notes', 'notiz', 'bemerkung']),
    categories: headerIndex(headerList, [
      'kategorien',
      'kategorie',
      'categories',
      'category',
      'tags',
    ]),
  };

  const usePositional = cols.name < 0 && cols.email < 0 && cols.company < 0;
  const result = [];

  for (const values of dataRows || []) {
    if (!Array.isArray(values)) continue;
    // Neue Export-Struktur vs. altes Layout ohne Header
    const positionalNew =
      usePositional && looksLikeEmail(values[4]) && !looksLikeEmail(values[1]);
    const positionalMap = positionalNew
      ? {
          name: 0,
          gender: 1,
          name2: 2,
          gender2: 3,
          email: 4,
          company: 5,
          phone: 6,
          website: 7,
          notes: 8,
          categories: 9,
        }
      : {
          name: 0,
          email: 1,
          company: 2,
          phone: 3,
          website: 4,
          notes: 5,
          gender: -1,
          name2: -1,
          gender2: -1,
          categories: -1,
        };

    const pick = (key, fallbackIndex) => {
      const idx = usePositional
        ? (positionalMap[key] ?? fallbackIndex)
        : cols[key];
      if (idx == null || idx < 0) return '';
      return cellToString(values[idx]);
    };
    const name2 = pick('name2', -1);
    const genderRaw = pick('gender', -1);
    const gender2Raw = pick('gender2', -1);
    const row = {
      name: pick('name', 0),
      gender: genderRaw ? parseContactGender(genderRaw) : '',
      name2,
      gender2: name2 && gender2Raw ? parseContactGender(gender2Raw) : '',
      email: pick('email', 1),
      company: pick('company', 2),
      phone: pick('phone', 3),
      website: pick('website', 4),
      notes: pick('notes', 5),
      categoryNames: parseCategoryNames(pick('categories', -1)),
    };
    if (row.name || row.email || row.company) {
      result.push(row);
    }
  }
  return result;
};

export const parseCSV = (text) => {
  const cleaned = String(text || '').replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const delimiter = detectCsvDelimiter(cleaned);
  const headers = parseCsvLine(lines[0], delimiter);
  const dataRows = lines.slice(1).map((line) => parseCsvLine(line, delimiter));
  return mapContactRowsFromTable(headers, dataRows);
};

export const parseXlsx = async (arrayBuffer) => {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];

  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    blankrows: false,
    raw: false,
  });
  if (!Array.isArray(matrix) || matrix.length < 2) return [];

  const headers = (matrix[0] || []).map((h) => cellToString(h));
  const dataRows = matrix
    .slice(1)
    .filter((row) => Array.isArray(row) && row.some((cell) => cellToString(cell)));
  return mapContactRowsFromTable(headers, dataRows);
};

export const parseContactImportFile = async (file) => {
  if (!file) return [];
  const name = String(file.name || '').toLowerCase();
  const isXlsx =
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel';

  if (isXlsx) {
    const buffer = await file.arrayBuffer();
    return parseXlsx(buffer);
  }

  const text = await file.text();
  return parseCSV(text);
};

/** Erkennung von Website-/Domain-Zellen (auch ohne http) */
const WEBSITE_FAKE_TLD =
  /^(ansprechpartner|geschlecht|unternehmen|telefon|notizen|kategorien|abgemeldet|erstellt|email|mail|firma|name|website|webseite|homepage|kontakt|company|phone|notes|category|categories|tags)$/i;

export const looksLikeWebsiteCell = (value) => {
  const t = String(value || '').trim();
  if (!t || t.length > 300 || /\s/.test(t) || t.includes('@')) return false;

  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      const host = String(u.hostname || '').replace(/^www\./i, '');
      if (!host.includes('.')) return false;
      const tld = host.split('.').pop() || '';
      if (WEBSITE_FAKE_TLD.test(tld)) return false;
      return true;
    } catch (_) {
      return false;
    }
  }

  if (/^www\./i.test(t)) {
    const host = t.replace(/^www\./i, '').split(/[/:?#]/)[0];
    const tld = host.split('.').pop() || '';
    return host.includes('.') && !WEBSITE_FAKE_TLD.test(tld);
  }

  // domain.tld[/pfad] — keine „2.Ansprechpartner“-Falschtreffer
  if (
    !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+(?:[/:?#].*)?$/i.test(
      t
    )
  ) {
    return false;
  }
  const host = t.split(/[/:?#]/)[0];
  const parts = host.split('.');
  const tld = parts[parts.length - 1] || '';
  if (tld.length < 2 || tld.length > 24) return false;
  if (WEBSITE_FAKE_TLD.test(tld)) return false;
  // reine Nummern-Labels wie "2.irgendwas" sind keine Domains
  if (/^\d+$/.test(parts[0] || '')) return false;
  return true;
};

const looksLikeContactHeaderRow = (row) => {
  const cells = (Array.isArray(row) ? row : [])
    .map((c) =>
      cellToString(c)
        .toLowerCase()
        .replace(/^\uFEFF/, '')
        .trim()
    )
    .filter(Boolean);
  if (!cells.length) return false;
  const hit = cells.filter((h) =>
    /^(ansprechpartner|2\.?\s*ansprechpartner|geschlecht|e-?mail|email|unternehmen|firma|telefon|website|webseite|url|homepage|notizen|kategorien|abgemeldet|erstellt am|name|company|phone|notes)$/i.test(
      h
    )
  ).length;
  return hit >= 2;
};

const normalizeWebsiteImportValue = (value) => {
  const t = String(value || '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t.replace(/^http:\/\//i, 'https://');
  if (/^www\./i.test(t)) return `https://${t}`;
  return `https://${t}`;
};

/**
 * CSV/XLSX → nur Websites. Liest Website-Spalte oder erste URL-ähnliche Zelle.
 */
export const mapWebsiteRowsFromTable = (headers, dataRows) => {
  const headerList = (headers || []).map((h) => cellToString(h));
  const websiteCol = headerIndex(headerList, [
    'website',
    'webseite',
    'url',
    'homepage',
    'domain',
    'internetseite',
  ]);
  const result = [];
  const seen = new Set();

  const pushWebsite = (raw) => {
    if (!looksLikeWebsiteCell(raw)) return;
    const website = normalizeWebsiteImportValue(raw);
    const key = website
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .toLowerCase()
      .replace(/\/$/, '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push({
      name: '',
      gender: '',
      name2: '',
      gender2: '',
      email: '',
      company: '',
      phone: '',
      website,
      notes: '',
      categoryNames: [],
    });
  };

  for (const values of dataRows || []) {
    if (!Array.isArray(values)) continue;
    if (looksLikeContactHeaderRow(values)) continue;
    if (websiteCol >= 0) {
      pushWebsite(cellToString(values[websiteCol]));
      continue;
    }
    let found = '';
    for (const cell of values) {
      const s = cellToString(cell);
      if (looksLikeWebsiteCell(s)) {
        found = s;
        break;
      }
    }
    if (found) pushWebsite(found);
  }

  return result;
};

export const parseWebsiteImportCsv = (text) => {
  const cleaned = String(text || '').replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const delimiter = detectCsvDelimiter(cleaned);
  const first = parseCsvLine(lines[0], delimiter);
  const rest = lines.slice(1).map((line) => parseCsvLine(line, delimiter));
  const allRows = [first, ...rest];

  if (lines.length === 1 && first.length === 1 && looksLikeWebsiteCell(first[0])) {
    return mapWebsiteRowsFromTable([], [first]);
  }

  // Kontakt-Export o.ä.: Header niemals als Website werten
  if (looksLikeContactHeaderRow(first)) {
    return mapWebsiteRowsFromTable(first, rest);
  }

  const dataRows = allRows.filter((row) => !looksLikeContactHeaderRow(row));
  const urlLikeCount = dataRows.filter(
    (row) => Array.isArray(row) && row.some((cell) => looksLikeWebsiteCell(cellToString(cell)))
  ).length;
  if (dataRows.length > 0 && urlLikeCount === dataRows.length) {
    return mapWebsiteRowsFromTable([], dataRows);
  }

  return mapWebsiteRowsFromTable(first, rest);
};

export const parseWebsiteImportXlsx = async (arrayBuffer) => {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];

  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    blankrows: false,
    raw: false,
  });
  if (!Array.isArray(matrix) || matrix.length < 1) return [];

  const headers = (matrix[0] || []).map((h) => cellToString(h));
  const dataRows = matrix
    .slice(1)
    .filter((row) => Array.isArray(row) && row.some((cell) => cellToString(cell)));

  if (looksLikeContactHeaderRow(headers)) {
    return mapWebsiteRowsFromTable(headers, dataRows);
  }

  const headerLooksLikeUrl = headers.some((h) => looksLikeWebsiteCell(h));
  if (headerLooksLikeUrl || matrix.length === 1) {
    return mapWebsiteRowsFromTable(
      [],
      matrix.filter((row) => Array.isArray(row) && row.some((cell) => cellToString(cell)))
    );
  }

  return mapWebsiteRowsFromTable(headers, dataRows);
};

export const parseWebsiteImportFile = async (file) => {
  if (!file) return [];
  const name = String(file.name || '').toLowerCase();
  const isXlsx =
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel';

  if (isXlsx) {
    const buffer = await file.arrayBuffer();
    return parseWebsiteImportXlsx(buffer);
  }

  const text = await file.text();
  return parseWebsiteImportCsv(text);
};

const websiteDedupeKeyFromUrl = (value) => {
  const t = String(value || '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .toLowerCase()
    .replace(/\/$/, '');
  return t;
};

/**
 * CSV/XLSX → nur E-Mail + Website. Andere Spalten werden ignoriert.
 */
export const mapEmailWebsiteRowsFromTable = (headers, dataRows) => {
  const headerList = (headers || []).map((h) => cellToString(h));
  let emailCol = headerIndex(headerList, ['e-mail', 'email', 'e mail', 'mail']);
  let websiteCol = headerIndex(headerList, [
    'website',
    'webseite',
    'url',
    'homepage',
    'domain',
    'internetseite',
  ]);
  const categoriesCol = headerIndex(headerList, [
    'kategorien',
    'kategorie',
    'categories',
    'category',
    'tags',
  ]);

  const usePositional = emailCol < 0 && websiteCol < 0;

  const result = [];
  const seen = new Set();

  for (const values of dataRows || []) {
    if (!Array.isArray(values)) continue;
    let emailRaw = emailCol >= 0 ? cellToString(values[emailCol]) : '';
    let websiteRaw = websiteCol >= 0 ? cellToString(values[websiteCol]) : '';

    if (usePositional) {
      const a = cellToString(values[0]);
      const b = cellToString(values[1]);
      if (looksLikeEmail(a) && !looksLikeEmail(b)) {
        emailRaw = a;
        websiteRaw = b;
      } else if (looksLikeEmail(b) && !looksLikeEmail(a)) {
        emailRaw = b;
        websiteRaw = a;
      } else if (looksLikeWebsiteCell(a) && looksLikeEmail(b)) {
        websiteRaw = a;
        emailRaw = b;
      } else {
        emailRaw = a;
        websiteRaw = b;
      }
    }

    const email = looksLikeEmail(emailRaw) ? String(emailRaw).trim().toLowerCase() : '';
    const website = looksLikeWebsiteCell(websiteRaw)
      ? normalizeWebsiteImportValue(websiteRaw)
      : '';
    if (!email && !website) continue;

    const key = email || websiteDedupeKeyFromUrl(website);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);

    const categoryNames =
      categoriesCol >= 0 ? parseCategoryNames(cellToString(values[categoriesCol])) : [];

    result.push({
      name: '',
      gender: '',
      name2: '',
      gender2: '',
      email,
      company: '',
      phone: '',
      website,
      notes: '',
      categoryNames,
    });
  }
  return result;
};

export const parseEmailWebsiteImportCsv = (text) => {
  const cleaned = String(text || '').replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const delimiter = detectCsvDelimiter(cleaned);
  const first = parseCsvLine(lines[0], delimiter);
  const hasHeader =
    looksLikeContactHeaderRow(first) ||
    headerIndex(
      first.map((h) => cellToString(h)),
      ['e-mail', 'email', 'e mail', 'mail', 'website', 'webseite', 'url', 'homepage']
    ) >= 0;
  if (hasHeader && lines.length < 2) return [];
  const headers = hasHeader ? first : [];
  const dataRows = (hasHeader ? lines.slice(1) : lines).map((line) =>
    parseCsvLine(line, delimiter)
  );
  return mapEmailWebsiteRowsFromTable(headers, dataRows);
};

export const parseEmailWebsiteImportXlsx = async (arrayBuffer) => {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    blankrows: false,
    raw: false,
  });
  if (!Array.isArray(matrix) || !matrix.length) return [];
  const headers = (matrix[0] || []).map((h) => cellToString(h));
  const dataRows = matrix
    .slice(1)
    .filter((row) => Array.isArray(row) && row.some((cell) => cellToString(cell)));
  if (looksLikeContactHeaderRow(headers) || headerIndex(headers, ['e-mail', 'email', 'mail', 'website', 'webseite']) >= 0) {
    return mapEmailWebsiteRowsFromTable(headers, dataRows);
  }
  return mapEmailWebsiteRowsFromTable(
    [],
    matrix.filter((row) => Array.isArray(row) && row.some((cell) => cellToString(cell)))
  );
};

export const parseEmailWebsiteImportFile = async (file) => {
  if (!file) return [];
  const name = String(file.name || '').toLowerCase();
  const isXlsx =
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel';

  if (isXlsx) {
    const buffer = await file.arrayBuffer();
    return parseEmailWebsiteImportXlsx(buffer);
  }

  const text = await file.text();
  return parseEmailWebsiteImportCsv(text);
};

export const exportCampaignsToCSV = (campaigns) => {
  const headers = [
    'Kontakt',
    'E-Mail',
    'Unternehmen',
    'Template',
    'Status',
    'Follow-ups',
    'Antwort erhalten',
    'Erstellt am',
  ];
  const rows = campaigns.map((c) => [
    csvEscape(c.contact_name || ''),
    csvEscape(c.contact_email || ''),
    csvEscape(c.contact_company || ''),
    csvEscape(c.template_name || ''),
    csvEscape(c.status || ''),
    c.follow_up_count || 0,
    c.reply_received || c.auto_reply_received ? 'Ja' : 'Nein',
    csvEscape(c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE') : ''),
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `kampagnen_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
